import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";

/**
 * NewProgramWizard — modal for creating a top-level program.
 *
 * Programs are the R&D container that hold projects and chats. The
 * backend POST /api/programs accepts `{ name, description?, id? }` and
 * returns the persisted program. Mirrors NewProjectWizard's shape with
 * a simpler form (no persona templates — those live on projects per
 * spec §11).
 *
 * On submit:
 *   1. call workspaceStore.createProgram(input)
 *   2. close the wizard
 *   3. navigate to /v2/p/<new-id> so the user lands on the empty
 *      program-home and can immediately create a first project.
 */
export const NewProgramWizardBase = ({
  workspaceStore,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setSubmitting(false);
    setError(null);
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === "Escape" && !submitting) onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!name.trim()) {
      setError("Program name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const program = await workspaceStore.createProgram({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onClose?.();
      navigate(`/v2/p/${program.id}`);
    } catch (err) {
      setError(err?.message || String(err));
      setSubmitting(false);
    }
  }, [submitting, name, description, workspaceStore, navigate, onClose]);

  if (!open) return null;

  return (
    <div className="wiz-root" data-testid="v2-new-program-wizard">
      <div
        className="wiz-backdrop"
        onClick={() => !submitting && onClose?.()}
        aria-hidden="true"
      />
      <div className="wiz-card" role="dialog" aria-label="New program">
        <header className="wiz-head">
          <h2 className="wiz-title">New program</h2>
          <button
            type="button"
            className="wiz-close"
            aria-label="Close"
            onClick={() => !submitting && onClose?.()}
            data-testid="v2-wiz-close"
          >
            ✕
          </button>
        </header>

        <form className="wiz-body" onSubmit={submit}>
          <label className="wiz-field">
            <span className="wiz-field__label">Name</span>
            <input
              type="text"
              className="wiz-field__input"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. KRAS dependency, DDR-α, Compound triage"
              data-testid="v2-wiz-name"
              disabled={submitting}
              maxLength={120}
            />
          </label>

          <label className="wiz-field">
            <span className="wiz-field__label">Description (optional)</span>
            <textarea
              className="wiz-field__textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note on the R&D focus area this program covers."
              rows={2}
              data-testid="v2-wiz-description"
              disabled={submitting}
              maxLength={400}
            />
          </label>

          <p className="wiz-tmpl-hint">
            Programs are long-lived containers for projects and chats. After creating one
            you'll land on its home page and can add a first project from there.
          </p>

          {error && <div className="wiz-error" data-testid="v2-wiz-error">{error}</div>}

          <footer className="wiz-foot">
            <button
              type="button"
              className="wiz-btn wiz-btn--ghost button-ghost"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              data-testid="v2-wiz-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wiz-btn wiz-btn--primary button-primary"
              disabled={submitting || !name.trim()}
              data-testid="v2-wiz-submit"
            >
              {submitting ? "Creating…" : "Create program"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

const NewProgramWizard = inject("workspaceStore")(observer(NewProgramWizardBase));
export default NewProgramWizard;
